/**
 * Klaviyo newsletter signup for form.liquid
 * Posts to the client Subscriptions API using the theme's public API key (company ID) and list ID.
 */

const KLAVIYO_REVISION = '2026-07-15';
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const isConfiguredValue = (value) => (value || '').trim() !== '';

export default {
  name: 'klaviyoForm',
  component(config = {}) {
    return {
      companyId: config.companyId || '',
      listId: config.listId || '',
      source: config.source || 'Newsletter',
      successMessage: config.successMessage || '',
      errorInvalidEmail: config.errorInvalidEmail || '',
      errorMissingConfig: config.errorMissingConfig || '',
      errorGeneric: config.errorGeneric || '',
      status: '',
      message: '',
      isSubmitting: false,

      getEmailFromForm(form) {
        const emailInput =
          form.querySelector('[name="contact[email]"]') ||
          form.querySelector('[name="email"]') ||
          form.querySelector('input[type="email"]');

        return (emailInput?.value || '').trim();
      },

      setStatus(status, message) {
        this.status = status;
        this.message = message;
      },

      setSubmitting(form, isSubmitting) {
        this.isSubmitting = isSubmitting;
        const submitButton = form.querySelector('[type="submit"]');
        if (submitButton) {
          submitButton.disabled = isSubmitting;
        }
      },

      parseKlaviyoError(responseBody) {
        if (!responseBody) {
          return this.errorGeneric;
        }

        try {
          const payload = JSON.parse(responseBody);
          const detail = payload?.errors?.[0]?.detail;
          if (detail) {
            return detail;
          }
        } catch (error) {
          if (window.enableDebugMode) {
            console.warn('[klaviyoForm] Could not parse Klaviyo error body', error);
          }
        }

        return this.errorGeneric;
      },

      async submit(event) {
        const form = event.target;

        if (this.isSubmitting) {
          return;
        }

        this.setStatus('', '');

        const email = this.getEmailFromForm(form);
        if (!email || !EMAIL_PATTERN.test(email)) {
          this.setStatus('error', this.errorInvalidEmail);
          return;
        }

        const companyId = this.companyId.trim();
        const listId = this.listId.trim();

        if (!isConfiguredValue(companyId) || !isConfiguredValue(listId)) {
          this.setStatus('error', this.errorMissingConfig);
          return;
        }

        this.setSubmitting(form, true);

        try {
          const response = await fetch(
            `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(companyId)}`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/vnd.api+json',
                Accept: 'application/vnd.api+json',
                revision: KLAVIYO_REVISION,
              },
              body: JSON.stringify({
                data: {
                  type: 'subscription',
                  attributes: {
                    custom_source: this.source,
                    profile: {
                      data: {
                        type: 'profile',
                        attributes: {
                          email,
                          subscriptions: {
                            email: {
                              marketing: {
                                consent: 'SUBSCRIBED',
                              },
                            },
                          },
                        },
                      },
                    },
                  },
                  relationships: {
                    list: {
                      data: {
                        type: 'list',
                        id: listId,
                      },
                    },
                  },
                },
              }),
            }
          );

          // Client subscriptions are queued: 202 is success for new and existing emails.
          if (response.status === 202) {
            this.setStatus('success', this.successMessage);
            form.reset();
            return;
          }

          const responseBody = await response.text();
          this.setStatus('error', this.parseKlaviyoError(responseBody));
        } catch (error) {
          if (window.enableDebugMode) {
            console.error('[klaviyoForm] Subscribe request failed', error);
          }
          this.setStatus('error', this.errorGeneric);
        } finally {
          this.setSubmitting(form, false);
        }
      },
    };
  },
};
